import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useMemo, useEffect } from 'react';
import { useOnSavedViewsReset } from '../hooks/useSavedView';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import CopyableLabel, { CopyIconButton, CopyAllButton, AddressActions } from './shared/CopyableLabel';
import { Edit, Trash2, Archive, X, Plus, Printer, Save, FileSpreadsheet, Database } from 'lucide-react';
import PermButton from './shared/PermButton';
import FloatingActions from './FloatingActions';
import { exportView, exportFullDump } from '../lib/exportXlsx';
import RecordReportDialog from './RecordReportDialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { SortableHeader, useSort, ResetViewButton } from './ui/SortableHeader';
import ViewToggle, { readStoredView } from './shared/ViewToggle';
import FileUpload from './FileUpload';
import AttachmentManager from './AttachmentManager';
import ContractsCrossRefPanel from './ContractsCrossRefPanel';
import useDeepShortcut from '../hooks/useDeepShortcut';
import { dateLocale } from '../lib/dateFormat';
import { DateText } from './ui/week-badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_CUSTOMER_FORM = {
  name: '', email: '', phone: '', address: '', url: '',
  invoice_address: '', tax_id: '', vat_number: '', payment_terms: 14,
  invoice_email: '', bank_details: '', invoice_notes: '',
  contacts: [],
};

const populateFormFromCustomer = (c) => ({
  name: c.name, email: c.email || '', phone: c.phone || '', address: c.address || '', url: c.url || '',
  invoice_address: c.invoice_address || '', tax_id: c.tax_id || '', vat_number: c.vat_number || '',
  payment_terms: c.payment_terms || 14, invoice_email: c.invoice_email || '',
  bank_details: c.bank_details || '', invoice_notes: c.invoice_notes || '',
  contacts: Array.isArray(c.contacts) ? c.contacts : [],
});

const CustomerFormFields = ({ form, setForm, onSubmit, submitLabel }) => {
  const { t } = useTranslation();
  const addContact = () => setForm(prev => ({ ...prev, contacts: [...(prev.contacts || []), { name: '', position: '', phone: '', email: '' }]  }));
  const updateContact = (idx, patch) => setForm(prev => ({
    ...prev,
    contacts: (prev.contacts || []).map((c, i) => (i === idx ? { ...c, ...patch } : c)),
  }));
  const removeContact = (idx) => setForm(prev => ({
    ...prev,
    contacts: (prev.contacts || []).filter((_, i) => i !== idx),
  }));

  return (
  <form onSubmit={onSubmit} className="space-y-4">
    <Tabs defaultValue="general" className="space-y-4">
      <TabsList className="grid grid-cols-3 w-full">
        <TabsTrigger value="general" data-testid="customer-tab-general">{t('auto.proj.general')}</TabsTrigger>
        <TabsTrigger value="invoicing" data-testid="customer-tab-invoicing">{t('auto.proj.invoicing')}</TabsTrigger>
        <TabsTrigger value="contacts" data-testid="customer-tab-contacts">{t('auto.proj.contacts')}</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="space-y-3">
    <div className="space-y-2">
      <Label>{t('auto.proj.customerName')}</Label>
      <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))} required />
    </div>
    <div className="space-y-2">
      <CopyableLabel label={t('auto.proj.email')} value={form.email} testId="copy-customer-email" />
      <Input type="email" value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value  }))} />
    </div>
    <div className="space-y-2">
      <CopyableLabel label={t('auto.proj.phone')} value={form.phone} testId="copy-customer-phone" />
      <Input value={form.phone} onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value  }))} />
    </div>
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <CopyableLabel label={t('auto.proj.address')} value={form.address} testId="copy-customer-address" />
        {form.invoice_address ? (
          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer" data-testid="customer-address-same-as-invoice-wrap">
            <input
              type="checkbox"
              className="rounded"
              checked={!!form.address && form.address === form.invoice_address}
              onChange={(e) => setForm(prev => ({ ...prev, address: e.target.checked ? form.invoice_address : form.address  }))}
              data-testid="customer-address-same-as-invoice"
            />
            {t('auto.proj.sameAsInvoiceAddress')}
          </label>
        ) : null}
      </div>
      <Input value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value  }))} />
    </div>
    <div className="space-y-2">
      <CopyableLabel label={t('auto.proj.webUrl')} value={form.url} testId="copy-customer-url" />
      <Input value={form.url} onChange={(e) => setForm(prev => ({ ...prev, url: e.target.value  }))} placeholder="https://example.com" data-testid="customer-url-input" />
    </div>
      </TabsContent>

      <TabsContent value="invoicing" className="space-y-3">
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <CopyableLabel label={t('auto.proj.taxIdNip')} value={form.tax_id} testId="copy-customer-tax-id" />
            <Input value={form.tax_id} onChange={(e) => setForm(prev => ({ ...prev, tax_id: e.target.value  }))} placeholder="e.g., 1234567890" data-testid="customer-tax-id" />
          </div>
          <div className="space-y-2">
            <CopyableLabel label={t('auto.proj.euVatNumber')} value={form.vat_number} testId="copy-customer-vat" />
            <Input value={form.vat_number} onChange={(e) => setForm(prev => ({ ...prev, vat_number: e.target.value  }))} placeholder="e.g., GB123456789" data-testid="customer-vat-number" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <CopyableLabel label={t('auto.proj.invoiceAddressIfDifferent')} value={form.invoice_address} testId="copy-customer-invoice-address" />
            {form.address ? (
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer" data-testid="customer-invoice-same-as-main-wrap">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={!!form.invoice_address && form.invoice_address === form.address}
                  onChange={(e) => setForm(prev => ({ ...prev, invoice_address: e.target.checked ? form.address : ''  }))}
                  data-testid="customer-invoice-same-as-main"
                />
                {t('auto.proj.sameAsMainAddress')}
              </label>
            ) : null}
          </div>
          <Input value={form.invoice_address} onChange={(e) => setForm(prev => ({ ...prev, invoice_address: e.target.value  }))} placeholder={t('auto.proj.leaveBlankToUseMainAddress')} />
        </div>
        <div className="space-y-2">
          <CopyableLabel label={t('auto.proj.invoiceEmail')} value={form.invoice_email} testId="copy-customer-invoice-email" />
          <Input value={form.invoice_email} onChange={(e) => setForm(prev => ({ ...prev, invoice_email: e.target.value  }))} placeholder="invoices@company.com" />
        </div>
        <div className="space-y-2">
          <CopyableLabel label={t('auto.proj.paymentTermsDays')} value={form.payment_terms} testId="copy-customer-payment-terms" />
          <Input type="number" value={form.payment_terms} onChange={(e) => setForm(prev => ({ ...prev, payment_terms: parseInt(e.target.value) || 14  }))} />
        </div>
        <div className="space-y-2">
          <CopyableLabel label={t('auto.proj.bankDetails')} value={form.bank_details} testId="copy-customer-bank" />
          <Input value={form.bank_details} onChange={(e) => setForm(prev => ({ ...prev, bank_details: e.target.value  }))} placeholder={t('auto.proj.customerSBankAccount')} />
        </div>
        <div className="space-y-2">
          <CopyableLabel label={t('auto.proj.invoiceNotes')} value={form.invoice_notes} testId="copy-customer-invoice-notes" />
          <Input value={form.invoice_notes} onChange={(e) => setForm(prev => ({ ...prev, invoice_notes: e.target.value  }))} placeholder={t('auto.proj.defaultNotesOnInvoices')} />
        </div>
      </div>
      </TabsContent>

      <TabsContent value="contacts" className="space-y-3">
        <p className="text-xs text-slate-500">{t('auto.proj.namedContactsAtThisCustomerThese')}</p>
        {(form.contacts || []).length === 0 && (
          <div className="text-sm text-slate-500 italic py-4 text-center border rounded-md" data-testid="customer-contacts-empty">{t('auto.proj.noContactsAddedYet')}</div>
        )}
        {(form.contacts || []).map((c, idx) => (
          <div key={idx} className="border rounded-md p-3 space-y-2 relative" data-testid={`customer-contact-${idx}`}>
            <button type="button" className="absolute top-2 right-2 text-slate-400 hover:text-red-600" onClick={() => removeContact(idx)} data-testid={`customer-contact-remove-${idx}`} aria-label="Remove contact"><X className="h-4 w-4" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <CopyableLabel label="Name" value={c.name} testId={`copy-customer-contact-${idx}-name`} />
                <Input value={c.name || ''} onChange={(e) => updateContact(idx, { name: e.target.value })} data-testid={`customer-contact-${idx}-name`} />
              </div>
              <div>
                <CopyableLabel label="Role / Position" value={c.position} testId={`copy-customer-contact-${idx}-position`} />
                <Input value={c.position || ''} onChange={(e) => updateContact(idx, { position: e.target.value })} data-testid={`customer-contact-${idx}-position`} />
              </div>
              <div>
                <CopyableLabel label="Email" value={c.email} testId={`copy-customer-contact-${idx}-email`} />
                <Input type="email" value={c.email || ''} onChange={(e) => updateContact(idx, { email: e.target.value })} data-testid={`customer-contact-${idx}-email`} />
              </div>
              <div>
                <CopyableLabel label="Phone" value={c.phone} testId={`copy-customer-contact-${idx}-phone`} />
                <Input value={c.phone || ''} onChange={(e) => updateContact(idx, { phone: e.target.value })} data-testid={`customer-contact-${idx}-phone`} />
              </div>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="customer-contact-add">
          <Plus className="h-4 w-4 mr-1" /> {t('auto.proj.addContact')}
        </Button>
      </TabsContent>
    </Tabs>
    <Button type="submit" className="w-full">{submitLabel}</Button>
    <FloatingActions actions={[{
      icon: Save, label: submitLabel, tone: 'primary',
      testId: 'customer-floating-save-btn', onClick: (e) => onSubmit(e),
    }]} />
  </form>
  );
};

/**
 * Edit / Delete / Archive triple for a single customer row. Extracted
 * to module scope so React's reconciler stays stable across cards ↔ list
 * view switches.
 */
const CustomerRowActions = ({
  customer,
  editOpen,
  setEditOpen,
  setEditingCustomer,
  form,
  setForm,
  handleEdit,
  handleDelete,
  handleArchive,
  fetchData,
  setReportCustomer,
}) => (
  <div className="flex flex-wrap justify-end gap-1 min-w-0">
    <Dialog open={editOpen === customer.id} onOpenChange={(open) => {
      if (!open) { setEditOpen(null); setEditingCustomer(null); setForm({ ...EMPTY_CUSTOMER_FORM }); }
      else { setEditOpen(customer.id); }
    }}>
      <DialogTrigger asChild>
        <PermButton perm="customers.edit" variant="outline" size="icon" className="h-8 w-8" data-testid={`edit-customer-${customer.id}`} onClick={() => {
          setEditingCustomer(customer);
          setForm(populateFormFromCustomer(customer));
          setEditOpen(customer.id);
        }}><Edit className="w-4 h-4" /></PermButton>
      </DialogTrigger>
      <Button variant="outline" size="icon" className="h-8 w-8" title="Print report" data-testid={`report-customer-${customer.id}`}
        onClick={(e) => { e.stopPropagation(); setReportCustomer(customer); }}>
        <Printer className="w-4 h-4" />
      </Button>
      <DialogContent className="max-w-[95vw] sm:max-w-[1400px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Customer - {customer.name}</DialogTitle>
          <DialogDescription>{i18n.t('auto.proj.updateCustomerInformationLogoAndAttachments')}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="details" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 w-full">
            <TabsTrigger value="details" data-testid="tab-details">{i18n.t('auto.proj.customerDetails')}</TabsTrigger>
            <TabsTrigger value="logo" data-testid="tab-logo">{i18n.t('auto.proj.logo')}</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments">{i18n.t('auto.proj.attachments')}</TabsTrigger>
            <TabsTrigger value="contracts" data-testid={`customer-contracts-tab-${customer.id}`}>Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="details">
            <CustomerFormFields form={form} setForm={setForm} onSubmit={handleEdit} submitLabel="Update Customer" />
          </TabsContent>
          <TabsContent value="logo" className="space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-4">{i18n.t('auto.proj.customerLogo')}</h3>
              <FileUpload type="logo" entityId={customer.id} currentFile={customer.logo_file}
                onUploadComplete={() => { toast.success(i18n.t('auto.proj.logoUpdated')); fetchData(); }}
                onUploadError={() => toast.error(i18n.t('auto.proj.failedToUploadLogo'))} />
            </div>
          </TabsContent>
          <TabsContent value="attachments">
            <AttachmentManager entityType="customer" entityId={customer.id} entityName={customer.name} />
          </TabsContent>
          <TabsContent value="contracts" className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-slate-800">{i18n.t('auto.proj.linkedDocuments')}</h3>
              <p className="text-xs text-slate-500">{i18n.t('auto.proj.contractsAnnexesSupportAndClientLetters3')}</p>
            </div>
            <ContractsCrossRefPanel scope="customer" id={customer.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <PermButton perm="customers.edit" variant="outline" size="icon" className="h-8 w-8" title="Delete permanently" data-testid={`delete-customer-${customer.id}`}><Trash2 className="w-4 h-4 text-red-500" /></PermButton>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{i18n.t('auto.proj.deleteCustomer')}</AlertDialogTitle>
          <AlertDialogDescription>Are you sure you want to delete {customer.name}? This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{i18n.t('auto.proj.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDelete(customer.id)}>{i18n.t('auto.proj.delete')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Button variant="outline" size="icon" className="h-8 w-8" title="Archive customer" data-testid={`archive-customer-${customer.id}`} onClick={() => handleArchive(customer.id, customer.name)}>
      <Archive className="w-4 h-4 text-orange-500" />
    </Button>
  </div>
);

const CustomersTab = ({ customers, user, fetchData, navContext }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...EMPTY_CUSTOMER_FORM });
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [view, setView] = useState(() => readStoredView('view:customers', 'cards'));
  const [reportCustomer, setReportCustomer] = useState(null);
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('name', 'asc', 'customers');

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/customers`, form);
      toast.success(`${res.data?.name || form.name || 'Customer'} created`);
      setCreateOpen(false);
      setForm({ ...EMPTY_CUSTOMER_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorCreatingCustomer') + (err.response?.data?.detail || err.message)); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${API}/customers/${editingCustomer.id}`, form);
      toast.success(`${res.data?.name || editingCustomer.name || 'Customer'} updated`);
      setEditingCustomer(null);
      setEditOpen(null);
      setForm({ ...EMPTY_CUSTOMER_FORM });
      fetchData();
    } catch (err) { toast.error(t('auto.proj.errorUpdatingCustomer') + (err.response?.data?.detail || err.message)); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success(t('auto.proj.customerDeleted'));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      if (msg.includes('associated projects')) toast.error(t('auto.proj.cannotDeleteCustomerPleaseDeleteAssociated'));
      else if (err.response?.status === 403) toast.error(t('auto.proj.permissionDeniedAdminOrManagerPrivileges'));
      else toast.error(t('auto.proj.errorDeletingCustomer') + msg);
    }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Archive ${name}? They will be hidden but can be restored from Settings > Archived.`)) return;
    try {
      await axios.put(`${API}/customers/${id}/archive`);
      toast.success(`${name} archived`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error archiving customer'); }
  };

  const filtered = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c => {
      const hay = [c.name, c.email, c.phone, c.tax_id, c.vat_number, c.address]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [customers, searchFilter]);

  const getSortValue = (c, key) => {
    switch (key) {
      case 'name': return (c.name || '').toLowerCase();
      case 'email': return (c.email || '').toLowerCase();
      case 'phone': return (c.phone || '').toLowerCase();
      case 'tax_id': return (c.tax_id || '').toLowerCase();
      case 'created_at': return c.created_at ? new Date(c.created_at).getTime() : 0;
      default: return '';
    }
  };
  const sorted = useMemo(() => [...filtered].sort(comparator(getSortValue)), [filtered, comparator]);

  const filtersActive = !!searchFilter;

  // Deep link from the global search palette.
  useEffect(() => {
    if (navContext?.searchCustomer) setSearchFilter(navContext.searchCustomer);
    if (navContext?.focusCustomerId) {
      const target = customers.find((c) => c.id === navContext.focusCustomerId);
      if (target) {
        setEditingCustomer(target);
        setForm(populateFormFromCustomer(target));
        setEditOpen(target.id);
      }
    }
  }, [navContext]);

  // Dashboard shortcuts: pin the customer record the user has open.
  useDeepShortcut({
    active: !!editOpen && !!editingCustomer,
    tab: 'customers',
    context: { focusCustomerId: editOpen },
    label: editingCustomer?.name,
    subtitle: 'Customers',
    icon: 'customers',
    perms: ['customers.view'],
    priority: 3,
  });
  const sortActive = sortKey !== 'name' || sortDir !== 'asc';
  const resetAll = () => {
    setSearchFilter('');
    resetSort();
  };

  useOnSavedViewsReset(resetAll);

  const rowActionProps = {
    editOpen,
    setEditOpen,
    setEditingCustomer,
    form,
    setForm,
    handleEdit,
    handleDelete,
    handleArchive,
    fetchData,
    setReportCustomer,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t('auto.proj.customerManagement')}</h2>
          <p className="text-slate-600">{t('auto.proj.manageCustomerRelationshipsAndInformation')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
          <ViewToggle storageKey="view:customers" value={view} onChange={setView} />
          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild><PermButton perm="customers.edit" className="shrink-0" data-testid="add-customer-btn">{t('auto.proj.addCustomer')}</PermButton></DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('auto.proj.addNewCustomer')}</DialogTitle>
                  <DialogDescription>{t('auto.proj.createANewCustomerRecord')}</DialogDescription>
                </DialogHeader>
                <CustomerFormFields form={form} setForm={setForm} onSubmit={handleCreate} submitLabel="Create Customer" />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
          <Input
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder={t('auto.proj.searchNameEmailTaxId')}
            className="pr-8"
            data-testid="customer-search-input"
          />
          {searchFilter && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              onClick={() => setSearchFilter('')}
              data-testid="customer-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <ResetViewButton
          show={filtersActive || sortActive}
          onReset={resetAll}
          testId="customers-reset-view-btn"
        />
        <div className="text-xs text-slate-500 ml-auto" data-testid="customers-result-count">
          {sorted.length} of {customers.length}
        </div>
      </div>

      {view === 'cards' ? (
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader><CardTitle>{t('auto.proj.customers')}</CardTitle><CardDescription>{t('auto.proj.clientDatabase')}</CardDescription></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {sorted.map((customer) => (
                <div
                  key={customer.id}
                  className={`p-4 border border-slate-200 rounded-lg ${isAdmin ? 'cursor-pointer hover:border-red-300 hover:bg-slate-50 transition-colors' : ''}`}
                  data-testid={`customer-card-${customer.id}`}
                  role={isAdmin ? 'button' : undefined}
                  tabIndex={isAdmin ? 0 : -1}
                  onClick={(e) => {
                    // Only open the edit dialog when clicking the card
                    // background — inner action buttons, links and inputs
                    // must keep their own click semantics.
                    if (!isAdmin) return;
                    if (e.target.closest('a,button,input,[role="dialog"]')) return;
                    setEditingCustomer(customer);
                    setForm(populateFormFromCustomer(customer));
                    setEditOpen(customer.id);
                  }}
                  onKeyDown={(e) => {
                    if (!isAdmin) return;
                    // Only react to Enter / Space when the card itself
                    // is the focused element. React events bubble up
                    // from portalled Dialog descendants; without this
                    // guard, typing a space inside a nested Input
                    // (e.g. the Contacts tab) would re-populate the
                    // form and wipe unsaved edits.
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setEditingCustomer(customer);
                      setForm(populateFormFromCustomer(customer));
                      setEditOpen(customer.id);
                    }
                  }}
                >
                  <div className="flex flex-col gap-2 mb-2">
                    <div className="flex items-start gap-3 min-w-0 w-full">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarImage src={customer.logo_file ? `${BACKEND_URL}/api/files/logo/${customer.logo_file}` : undefined} alt={customer.name} />
                        <AvatarFallback className="bg-slate-200 text-slate-600">{customer.name?.charAt(0)?.toUpperCase() || 'C'}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 break-words">{customer.name}</h3>
                          <CopyAllButton testId={`copy-all-customer-card-${customer.id}`} title={customer.name}
                            lines={[
                              ['Name', customer.name],
                              ['Email', customer.email],
                              ['Phone', customer.phone],
                              ['Tax ID', customer.tax_id],
                              ['Address', customer.address],
                              ['Website', customer.url],
                            ]} />
                        </div>
                        {customer.email && (
                          <p className="text-sm text-slate-600 break-all flex items-center gap-1">
                            {customer.email}
                            <CopyIconButton label="Email" value={customer.email} testId={`copy-customer-email-${customer.id}`} />
                          </p>
                        )}
                        {customer.url && (
                          <a href={customer.url.startsWith('http') ? customer.url : `https://${customer.url}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline truncate block"
                            data-testid={`customer-url-${customer.id}`}>{customer.url}</a>
                        )}
                      </div>
                    </div>
                    {isAdmin && <CustomerRowActions customer={customer} {...rowActionProps} />}
                  </div>
                  <div className="space-y-1">
                    {customer.phone && (
                      <p className="text-sm text-slate-600 break-words flex items-center gap-1">
                        <span className="text-slate-400">{t('auto.proj.phone2')}</span> {customer.phone}
                        <CopyIconButton label="Phone" value={customer.phone} testId={`copy-customer-phone-${customer.id}`} />
                      </p>
                    )}
                    {customer.tax_id && (
                      <p className="text-sm text-slate-600 break-words flex items-center gap-1">
                        <span className="text-slate-400">{t('auto.proj.taxId')}</span> {customer.tax_id}
                        <CopyIconButton label="Tax ID" value={customer.tax_id} testId={`copy-customer-tax-${customer.id}`} />
                      </p>
                    )}
                    {customer.address && (
                      <p className="text-sm text-slate-600 break-words flex items-start gap-1">
                        <span className="text-slate-400 shrink-0">{t('auto.proj.address2')}</span>
                        <span className="break-words">{customer.address}</span>
                        <AddressActions parts={[customer.address]} testId={`copy-customer-address-${customer.id}`} />
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {sorted.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">{t('auto.proj.noCustomersMatchTheCurrentFilters')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-0 shadow-sm">
          <Table data-testid="customers-list-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44px]"></TableHead>
                <SortableHeader column="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="customers-sort">{t('auto.proj.name')}</SortableHeader>
                <SortableHeader column="email" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="customers-sort" className="hidden md:table-cell">{t('auto.proj.email')}</SortableHeader>
                <SortableHeader column="phone" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="customers-sort" className="hidden lg:table-cell">{t('auto.proj.phone')}</SortableHeader>
                <SortableHeader column="tax_id" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="customers-sort" className="hidden lg:table-cell">{t('auto.proj.taxId2')}</SortableHeader>
                <SortableHeader column="created_at" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} testIdPrefix="customers-sort" className="hidden xl:table-cell">{t('auto.proj.added')}</SortableHeader>
                <TableHead className="text-right w-[116px] sm:w-[156px] lg:w-[196px]">{t('auto.proj.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((customer) => (
                <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}
                  className={isAdmin ? 'cursor-pointer hover:bg-slate-50' : ''}
                  onClick={(e) => {
                    if (!isAdmin) return;
                    if (e.target.closest('a,button,input,[role="dialog"]')) return;
                    setEditingCustomer(customer);
                    setForm(populateFormFromCustomer(customer));
                    setEditOpen(customer.id);
                  }}
                >
                  <TableCell className="align-top">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={customer.logo_file ? `${BACKEND_URL}/api/files/logo/${customer.logo_file}` : undefined} alt={customer.name} />
                      <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">{customer.name?.charAt(0)?.toUpperCase() || 'C'}</AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium text-slate-900 align-top break-words">
                    <div className="break-words">{customer.name}</div>
                    {customer.url && (
                      <a href={customer.url.startsWith('http') ? customer.url : `https://${customer.url}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline truncate block">{customer.url}</a>
                    )}
                    {/* Columns hidden on small screens fold in under the name. */}
                    <div className="md:hidden text-xs text-slate-500 break-all flex items-center gap-1 flex-wrap">
                      {customer.email}
                      {customer.email && <CopyIconButton label="Email" value={customer.email} testId={`copy-fold-customer-email-${customer.id}`} />}
                    </div>
                    <div className="lg:hidden text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                      {customer.phone}
                      {customer.phone && <CopyIconButton label="Phone" value={customer.phone} testId={`copy-fold-customer-phone-${customer.id}`} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600 break-all hidden md:table-cell align-top">
                    <span className="inline-flex items-center gap-1 flex-wrap">
                      {customer.email || '—'}
                      <CopyIconButton label="Email" value={customer.email} testId={`copy-row-customer-email-${customer.id}`} />
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 hidden lg:table-cell align-top">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      {customer.phone || '—'}
                      <CopyIconButton label="Phone" value={customer.phone} testId={`copy-row-customer-phone-${customer.id}`} />
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 hidden lg:table-cell align-top">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      {customer.tax_id || '—'}
                      <CopyIconButton label="Tax ID" value={customer.tax_id} testId={`copy-row-customer-tax-${customer.id}`} />
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs hidden xl:table-cell align-top">{customer.created_at ? <DateText value={customer.created_at} /> : '—'}</TableCell>
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1 flex-wrap w-[112px] sm:w-[152px] lg:w-[188px] ml-auto">
                      <CopyAllButton testId={`copy-all-customer-${customer.id}`} title={customer.name}
                        lines={[
                          ['Name', customer.name],
                          ['Email', customer.email],
                          ['Phone', customer.phone],
                          ['Tax ID', customer.tax_id],
                          ['Address', customer.address],
                          ['Website', customer.url],
                        ]} />
                      <AddressActions parts={[customer.address]} testId={`copy-row-customer-address-${customer.id}`} />
                      {isAdmin && <CustomerRowActions customer={customer} {...rowActionProps} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-500 text-sm">
                    {t('auto.proj.noCustomersMatchTheCurrentFilters')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      <RecordReportDialog
        open={!!reportCustomer}
        onOpenChange={(o) => !o && setReportCustomer(null)}
        kind="customer"
        recordId={reportCustomer?.id}
        recordName={reportCustomer?.name}
      />

      <FloatingActions actions={[
        { icon: Plus, label: t('auto.lbl.addCustomer'), tone: 'primary',
          testId: 'customers-floating-add-btn', hidden: !isAdmin,
          onClick: () => setCreateOpen(true) },
        { icon: FileSpreadsheet, label: t('auto.lbl.exportThisViewToExcel'),
          testId: 'customers-floating-export-btn',
          onClick: () => exportView({
            filename: 'customers', sheetName: 'Customers', rows: sorted,
            columns: [
              { header: t('auto.lbl.name'), key: 'name' },
              { header: t('auto.lbl.email'), key: 'email' },
              { header: t('auto.lbl.phone'), key: 'phone' },
              { header: t('auto.lbl.taxId'), key: 'tax_id' },
              { header: t('auto.lbl.vatNumber'), key: 'vat_number' },
              { header: t('auto.lbl.address'), key: 'address' },
              { header: t('auto.lbl.invoiceAddress'), key: 'invoice_address' },
              { header: t('auto.lbl.website'), key: 'url' },
              { header: t('auto.lbl.added'), key: 'created_at' },
            ],
          }) },
        { icon: Database, label: t('auto.lbl.exportAllCustomersWithEveryField'),
          testId: 'customers-floating-export-all-btn', hidden: !isAdmin,
          onClick: () => exportFullDump({ filename: 'customers', sheetName: 'Customers', rows: customers }) },
      ]} />
    </div>
  );
};

export default CustomersTab;
